// FindingsTab.js — All active findings across scan types (Discovery + Vuln Scan + Deep Security)
// PRD-37 Session 2: Cross-scan-type findings view with source/severity filtering

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getAssetFindings } from '../../services/assetService';
import { getFindingDetail } from '../../services/driftService';
import HttpTransactionView from '../common/HttpTransactionView';
import FINDING_EXPLANATIONS from '../../data/findingExplanations';
import './FindingsTab.css';

const SEVERITY_WEIGHT = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
const SEVERITY_LEVELS = ['critical', 'high', 'medium', 'low', 'info'];
const SEVERITY_LABELS = { critical: 'Crit', high: 'High', medium: 'Med', low: 'Low', info: 'Info' };

// Map template categories to high-level source groups
const SOURCE_GROUPS = {
    discovery: 'discovery',
    core: 'vuln-scan',
    ssh: 'vuln-scan',
    eol: 'vuln-scan',
    dast: 'deep-security',
    'ssl-deep': 'deep-security',
    nikto: 'deep-security',
    'nmap-vuln': 'deep-security',
    other: 'other'
};

// Human-readable source labels per template category
const SOURCE_LABELS = {
    discovery: 'Discovery',
    core: 'Vuln Scan',
    dast: 'Deep Security (DAST)',
    'ssl-deep': 'Deep Security (SSL)',
    ssh: 'Vuln Scan (SSH)',
    eol: 'Vuln Scan (EOL)',
    nikto: 'Deep Security (Nikto)',
    'nmap-vuln': 'Deep Security (Nmap)',
    other: 'Other'
};

// Source chip colors
const SOURCE_CHIP_COLORS = {
    discovery: { label: 'Discovery', color: '#6B7FFF' },
    'vuln-scan': { label: 'Vuln Scan', color: '#00D9FF' },
    'deep-security': { label: 'Deep Security', color: '#FF6B35' },
    other: { label: 'Other', color: '#666' }
};

const SOURCE_FILTER_OPTIONS = [
    { key: 'all', label: 'All Sources' },
    { key: 'discovery', label: 'Discovery' },
    { key: 'vuln-scan', label: 'Vuln Scan' },
    { key: 'deep-security', label: 'Deep Security' }
];

const FindingsTab = ({ assetId }) => {
    const [findings, setFindings] = useState([]);
    const [summary, setSummary] = useState({ critical: 0, high: 0, medium: 0, low: 0, info: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Filter state
    const [activeFilters, setActiveFilters] = useState(null);
    const [sourceFilter, setSourceFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [sortBy, setSortBy] = useState('severity');
    const [allExpanded, setAllExpanded] = useState(null);

    const loadFindings = useCallback(async () => {
        if (!assetId) return;
        setLoading(true);
        setError(null);
        try {
            const result = await getAssetFindings(assetId, { state: 'active' });
            setFindings(result.findings || []);
            setSummary(result.summary?.bySeverity || { critical: 0, high: 0, medium: 0, low: 0, info: 0 });
        } catch (err) {
            console.error('Failed to load findings:', err);
            setError(err.message || 'Failed to load findings');
        } finally {
            setLoading(false);
        }
    }, [assetId]);

    useEffect(() => { loadFindings(); }, [loadFindings]);

    // Initialize smart default filters (hide info/low when >50)
    useEffect(() => {
        if (findings.length > 0 && activeFilters === null) {
            if (findings.length > 50) {
                setActiveFilters(new Set(['critical', 'high', 'medium']));
            } else {
                setActiveFilters(new Set(['critical', 'high', 'medium', 'low', 'info']));
            }
        }
    }, [findings.length, activeFilters]);

    const ALL_SEVERITIES = useMemo(() => new Set(['critical', 'high', 'medium', 'low', 'info']), []);
    const effectiveFilters = activeFilters || ALL_SEVERITIES;

    // Debounced search
    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchInput(value);
        clearTimeout(window._ftSearchTimeout);
        window._ftSearchTimeout = setTimeout(() => {
            setSearchQuery(value);
        }, 300);
    }, []);

    const handleClearSearch = () => {
        setSearchInput('');
        setSearchQuery('');
    };

    // Filter + sort
    const processedFindings = useMemo(() => {
        let result = findings;

        // Source filter
        if (sourceFilter !== 'all') {
            result = result.filter(f => {
                const group = SOURCE_GROUPS[f.templateCategory] || 'other';
                return group === sourceFilter;
            });
        }

        // Severity filter
        result = result.filter(f => effectiveFilters.has((f.severity || 'info').toLowerCase()));

        // Search
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(f => {
                const searchFields = [
                    f.name, f.description, f.matchedAt, f.templateId,
                    (f.tags || []).join(' '), (f.cves || []).join(' ')
                ].filter(Boolean).join(' ').toLowerCase();
                return searchFields.includes(q);
            });
        }

        // Sort
        result = [...result].sort((a, b) => {
            if (sortBy === 'severity') {
                const diff = (SEVERITY_WEIGHT[a.severity] || 4) - (SEVERITY_WEIGHT[b.severity] || 4);
                if (diff !== 0) return diff;
                return (a.matchedAt || '').localeCompare(b.matchedAt || '');
            }
            if (sortBy === 'source') {
                const srcA = SOURCE_LABELS[a.templateCategory] || 'Other';
                const srcB = SOURCE_LABELS[b.templateCategory] || 'Other';
                return srcA.localeCompare(srcB);
            }
            if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
            if (sortBy === 'matchedAt') return (a.matchedAt || '').localeCompare(b.matchedAt || '');
            return 0;
        });

        return result;
    }, [findings, sourceFilter, effectiveFilters, searchQuery, sortBy]);

    // Source counts for filter badges
    const sourceCounts = useMemo(() => {
        const counts = { all: findings.length, discovery: 0, 'vuln-scan': 0, 'deep-security': 0 };
        for (const f of findings) {
            const group = SOURCE_GROUPS[f.templateCategory] || 'other';
            if (counts[group] !== undefined) counts[group]++;
        }
        return counts;
    }, [findings]);

    const handleToggleFilter = (severity) => {
        setActiveFilters(prev => {
            const next = new Set(prev || ['critical', 'high', 'medium', 'low', 'info']);
            if (next.has(severity)) next.delete(severity);
            else next.add(severity);
            return next;
        });
    };

    const handleShowAll = () => {
        setActiveFilters(new Set(['critical', 'high', 'medium', 'low', 'info']));
    };

    const allSeverityActive = SEVERITY_LEVELS.every(s => effectiveFilters.has(s));

    if (loading) {
        return <div className="ft-loading">Loading findings...</div>;
    }

    if (error) {
        return (
            <div className="ft-empty-state">
                <p>Unable to load findings.</p>
                <p className="ft-error-msg">{error}</p>
                <button className="ft-retry-btn" onClick={loadFindings}>Retry</button>
            </div>
        );
    }

    if (findings.length === 0) {
        return (
            <div className="ft-empty-state">
                <h3>No Active Findings</h3>
                <p>This asset has no active vulnerability or exposure findings. Run a scan to detect potential issues.</p>
            </div>
        );
    }

    return (
        <div className="ft-tab">
            {/* Summary Bar */}
            <div className="ft-summary-bar">
                <span className="ft-summary-title">
                    Active Findings &mdash; {findings.length} total
                </span>
                <div className="ft-severity-toggles">
                    {SEVERITY_LEVELS.map(sev => {
                        const count = summary[sev] || 0;
                        const isActive = effectiveFilters.has(sev);
                        return (
                            <button
                                key={sev}
                                className={`ft-severity-toggle ft-sev-${sev} ${isActive ? 'active' : 'dimmed'}`}
                                onClick={() => handleToggleFilter(sev)}
                                title={`${isActive ? 'Hide' : 'Show'} ${sev} findings`}
                            >
                                <span className="ft-sev-count">{count}</span>
                                <span className="ft-sev-label">{SEVERITY_LABELS[sev]}</span>
                            </button>
                        );
                    })}
                </div>
                {!allSeverityActive && (
                    <button className="ft-show-all-btn" onClick={handleShowAll}>Show All</button>
                )}
            </div>

            {/* Source Filter */}
            <div className="ft-source-filter">
                {SOURCE_FILTER_OPTIONS.map(opt => (
                    <button
                        key={opt.key}
                        className={`ft-source-btn ${sourceFilter === opt.key ? 'active' : ''}`}
                        onClick={() => setSourceFilter(opt.key)}
                    >
                        {opt.label}
                        <span className="ft-source-count">{sourceCounts[opt.key] || 0}</span>
                    </button>
                ))}
            </div>

            {/* Controls Row */}
            <div className="ft-controls-row">
                <div className="ft-search-wrapper">
                    <input
                        type="text"
                        className="ft-search-input"
                        placeholder="Search findings..."
                        value={searchInput}
                        onChange={handleSearchChange}
                    />
                    {searchInput && (
                        <button className="ft-search-clear" onClick={handleClearSearch}>&times;</button>
                    )}
                </div>

                <select
                    className="ft-sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                >
                    <option value="severity">Sort: Severity</option>
                    <option value="source">Sort: Source</option>
                    <option value="name">Sort: Name</option>
                    <option value="matchedAt">Sort: Target</option>
                </select>

                <div className="ft-expand-controls">
                    <button
                        className={`ft-expand-btn ${allExpanded === true ? 'active' : ''}`}
                        onClick={() => setAllExpanded(true)}
                        title="Expand all findings"
                    >Expand All</button>
                    <button
                        className={`ft-expand-btn ${allExpanded === false ? 'active' : ''}`}
                        onClick={() => setAllExpanded(false)}
                        title="Collapse all findings"
                    >Collapse All</button>
                </div>
            </div>

            {/* Filter Status */}
            {processedFindings.length < findings.length && (
                <div className="ft-filter-status">
                    Showing {processedFindings.length} of {findings.length} findings
                    {searchInput && <span> matching &ldquo;{searchInput}&rdquo;</span>}
                </div>
            )}

            {/* Findings List */}
            <div className="ft-findings-container">
                {processedFindings.length > 0 ? (
                    <div className="ft-findings-list">
                        {processedFindings.map((finding) => (
                            <FindingCard
                                key={finding.findingId}
                                finding={finding}
                                assetId={assetId}
                                allExpanded={allExpanded}
                                onToggle={() => setAllExpanded(null)}
                            />
                        ))}
                    </div>
                ) : (
                    <p className="ft-no-match">No findings match the current filters.</p>
                )}
            </div>
        </div>
    );
};

// Finding Card sub-component
const FindingCard = ({ finding, assetId, allExpanded, onToggle }) => {
    const [localExpanded, setLocalExpanded] = useState(false);
    const expanded = allExpanded !== null ? allExpanded : localExpanded;
    const severityClass = `ft-severity-${(finding.severity || 'info').toLowerCase()}`;
    const sourceGroup = SOURCE_GROUPS[finding.templateCategory] || 'other';
    const chipInfo = SOURCE_CHIP_COLORS[sourceGroup];
    const sourceLabel = SOURCE_LABELS[finding.templateCategory] || 'Other';
    const explanation = FINDING_EXPLANATIONS[finding.templateId];

    // Lazy-load HTTP transaction detail on first expand. Cached per card; collapse + re-expand reuses.
    const [detail, setDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [detailError, setDetailError] = useState(null);

    useEffect(() => {
        if (!expanded || detail || loadingDetail || detailError) return;
        if (!assetId || !finding.findingId) return;
        let cancelled = false;
        setLoadingDetail(true);
        getFindingDetail(assetId, finding.findingId)
            .then(result => {
                if (cancelled) return;
                if (result) setDetail(result);
                else setDetailError('No detail available');
            })
            .catch(err => { if (!cancelled) setDetailError(err?.message || 'Failed to load detail'); })
            .finally(() => { if (!cancelled) setLoadingDetail(false); });
        return () => { cancelled = true; };
    }, [expanded, assetId, finding.findingId]); // eslint-disable-line react-hooks/exhaustive-deps -- stable guards: detail/loadingDetail/detailError are state-machine guards, not inputs. Adding them to deps causes self-cancellation, see PRD-86.

    const handleToggle = () => {
        setLocalExpanded(!expanded);
        if (allExpanded !== null && onToggle) onToggle();
    };

    return (
        <div className={`ft-finding-card ${severityClass}`}>
            <div className="ft-finding-header" onClick={handleToggle}>
                <span className={`ft-severity-badge ${severityClass}`}>
                    {(finding.severity || 'INFO').toUpperCase()}
                </span>
                {chipInfo && (
                    <span
                        className="ft-source-chip"
                        style={{
                            color: chipInfo.color,
                            borderColor: chipInfo.color + '55',
                            background: chipInfo.color + '1A'
                        }}
                    >{chipInfo.label}</span>
                )}
                <span className="ft-finding-name">{finding.name || finding.templateId}</span>
                <span className="ft-finding-toggle">{expanded ? '\u25BE' : '\u25B8'}</span>
            </div>
            {expanded && (
                <div className="ft-finding-details">
                    {finding.description && (
                        <div className="ft-detail-row">
                            <span className="ft-detail-label">Description</span>
                            <span className="ft-detail-value">{finding.description}</span>
                        </div>
                    )}
                    {finding.matchedAt && (
                        <div className="ft-detail-row">
                            <span className="ft-detail-label">Target</span>
                            <span className="ft-detail-value ft-mono">{finding.matchedAt}</span>
                        </div>
                    )}
                    <div className="ft-detail-row">
                        <span className="ft-detail-label">Source</span>
                        <span className="ft-detail-value">{sourceLabel}</span>
                    </div>
                    {finding.templateId && (
                        <div className="ft-detail-row">
                            <span className="ft-detail-label">Template</span>
                            <span className="ft-detail-value ft-mono">{finding.templateId}</span>
                        </div>
                    )}
                    {finding.firstSeenAt && (
                        <div className="ft-detail-row">
                            <span className="ft-detail-label">First Seen</span>
                            <span className="ft-detail-value">{new Date(finding.firstSeenAt).toLocaleString()}</span>
                        </div>
                    )}
                    {finding.lastSeenAt && (
                        <div className="ft-detail-row">
                            <span className="ft-detail-label">Last Seen</span>
                            <span className="ft-detail-value">{new Date(finding.lastSeenAt).toLocaleString()}</span>
                        </div>
                    )}
                    {finding.tags && finding.tags.length > 0 && (
                        <div className="ft-detail-row">
                            <span className="ft-detail-label">Tags</span>
                            <div className="ft-tags">
                                {finding.tags.map((tag, i) => (
                                    <span key={i} className="ft-tag">{tag}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    {finding.cves && finding.cves.length > 0 && (
                        <div className="ft-detail-row">
                            <span className="ft-detail-label">CVEs</span>
                            <div className="ft-tags">
                                {finding.cves.map((cve, i) => (
                                    <span key={i} className="ft-tag">{cve}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    {finding.reference && (Array.isArray(finding.reference) ? finding.reference.length > 0 : finding.reference) && (
                        <div className="ft-detail-row">
                            <span className="ft-detail-label">Reference</span>
                            <span className="ft-detail-value">{
                                Array.isArray(finding.reference)
                                    ? finding.reference.join(', ')
                                    : finding.reference
                            }</span>
                        </div>
                    )}

                    {/* Finding Explanation */}
                    {explanation && (
                        <div className="ft-explanation">
                            <div className="ft-explanation-header">{explanation.title}</div>
                            <div className="ft-explanation-section">
                                <span className="ft-explanation-label">What this means</span>
                                <p>{explanation.explanation}</p>
                            </div>
                            <div className="ft-explanation-section">
                                <span className="ft-explanation-label">Impact</span>
                                <p>{explanation.impact}</p>
                            </div>
                            <div className="ft-explanation-section">
                                <span className="ft-explanation-label">Remediation</span>
                                <p>{explanation.remediation}</p>
                            </div>
                        </div>
                    )}

                    {/* HTTP Transaction (lazy-loaded via /finding-detail) */}
                    {loadingDetail && (
                        <div className="ft-detail-row"><span className="ft-detail-value">Loading HTTP transaction...</span></div>
                    )}
                    {detailError && (
                        <div className="ft-detail-row"><span className="ft-detail-value">HTTP transaction unavailable: {detailError}</span></div>
                    )}
                    {!loadingDetail && !detailError && detail && (
                        <HttpTransactionView httpTransaction={detail.httpTransaction} showEmpty />
                    )}
                </div>
            )}
        </div>
    );
};

export default FindingsTab;
